import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import { HTMLProps, forwardRef } from 'react';

import { cn } from '@/lib/css-utils';

import { BaseUIComponentProps } from '../base';

export type TextAreaVariant = 'outline' | 'filled';

const wrapperVariants = cva(
    classNames(
        'p-2 rounded-lg border has-[textarea:disabled]:cursor-not-allowed',
        'has-[textarea:focus-within]:outline-none group',
        'flex items-center justify-start gap-2'
    ),
    {
        variants: {
            variant: {
                outline: 'bg-transparent',
                filled: 'bg-token-fill-input-dark',
            },
            hasError: {
                true: 'border-nError-500 text-nError-500 has-[textarea:disabled]:opacity-50',
                false: classNames(
                    'border-neutral-600 hover:border-neutral-400 has-[textarea:focus-within]:border-neutral-400',
                    'has-[textarea:disabled]:border-neutral-600 has-[textarea:disabled]:hover:border-neutral-600 has-[textarea:disabled]:has-[textarea:focus-within]:border-neutral-600',

                    'text-neutral-200',
                    'has-[textarea:disabled]:text-neutral-500 has-[textarea:disabled]:hover:text-neutral-500',
                    'hover:text-neutral-50 has-[textarea:focus-within]:text-neutral-50'
                ),
            },
        },
        defaultVariants: {
            variant: 'outline',
            hasError: false,
        },
    }
);

const textareaVariants = cva(
    'focus:outline-none text-xs leading-4.5 px-2 font-roboto bg-transparent p-0 grow disabled:cursor-not-allowed resize-none',
    {
        variants: {
            hasError: {
                true: 'text-nError-500 placeholder-nError-500',
                false: classNames(
                    'text-neutral-200 focus:text-neutral-50 group-hover:text-neutral-50 disabled:text-neutral-500 disabled:group-hover:text-neutral-500',
                    'placeholder-neutral-200 group-hover:placeholder-neutral-50 disabled:placeholder-neutral-500 disabled:group-hover:placeholder-neutral-500'
                ),
            },
        },
        defaultVariants: {
            hasError: false,
        },
    }
);

export type TextAreaProps = {
    hasError?: boolean;
    disabled?: boolean;

    variant?: TextAreaVariant;

    wrapperClassName?: string;

    inputRef?: React.Ref<HTMLTextAreaElement>;
} & BaseUIComponentProps &
    Omit<HTMLProps<HTMLTextAreaElement>, 'prefix' | 'suffix'>;

export const TextArea = forwardRef<HTMLDivElement, TextAreaProps>(
    ({ hasError = false, disabled = false, className, wrapperClassName, variant, inputRef, ...props }, ref) => {
        return (
            <div
                className={wrapperVariants({
                    hasError,
                    variant,
                    className: wrapperClassName,
                })}
                ref={ref}
            >
                <textarea
                    className={cn(textareaVariants({ hasError }), className)}
                    disabled={disabled}
                    ref={inputRef}
                    {...props}
                />
            </div>
        );
    }
);

TextArea.displayName = 'TextArea';

export const Textarea = TextArea;
